/**
 * Lightweight i18n provider for the TapNpay PWA.
 *
 * Closes ULTRAREVIEW_2026-05-01 [Axe 6 P1]: pre-Vague-F.2.2 the PWA had
 * no i18n primitives, every screen hard-coded French, and `<html lang>`
 * was lying about the active locale.
 *
 * Why no react-intl / i18next:
 *   - bundle: each adds ~30 kB pre-gzip; we only need 3 locales and a
 *     tiny interpolation grammar.
 *   - the entire visible vocabulary fits in one `messages.ts`; we don't
 *     need on-demand JSON loading.
 *
 * Behaviour:
 *   - persists the chosen locale in localStorage under `tapnpay.locale`,
 *     so a returning guest doesn't have to switch every visit.
 *   - syncs `<html lang>` and `<html dir>` on every change — `dir="rtl"`
 *     for Arabic so the existing Tailwind layout flips automatically.
 *   - renders nothing different until the first effect runs (default = fr).
 */

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Locale,
  MessageKey,
  RTL_LOCALES,
  messages,
} from './messages';

const STORAGE_KEY = 'tapnpay.locale';

type Vars = Record<string, string | number>;

interface I18nContextValue {
  locale: Locale;
  dir: 'ltr' | 'rtl';
  setLocale: (next: Locale) => void;
  t: (key: MessageKey, vars?: Vars) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function pickInitialLocale(): Locale {
  if (typeof window === 'undefined') return 'fr';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'fr' || stored === 'en' || stored === 'ar') return stored;
  } catch {
    /* localStorage may throw under privacy mode — ignore. */
  }
  // Prefer a navigator language match before falling back to fr.
  const lang = (typeof navigator !== 'undefined' ? navigator.language : '').slice(0, 2);
  if (lang === 'en' || lang === 'fr' || lang === 'ar') return lang;
  return 'fr';
}

function resolveKey(locale: Locale, key: MessageKey): string {
  const parts = key.split('.');
  let cursor: unknown = messages[locale];
  for (const part of parts) {
    if (typeof cursor !== 'object' || cursor === null) return key;
    cursor = (cursor as Record<string, unknown>)[part];
    if (cursor == null) return key;
  }
  return typeof cursor === 'string' ? cursor : key;
}

function interpolate(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(/\{\{(\w+)\}\}/g, (_, k: string) => {
    const v = vars[k];
    return v === undefined ? `{{${k}}}` : String(v);
  });
}

interface I18nProviderProps {
  children: ReactNode;
  initialLocale?: Locale;
}

export function I18nProvider({ children, initialLocale }: I18nProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale ?? pickInitialLocale);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* swallow */
    }
  }, []);

  const dir: 'ltr' | 'rtl' = RTL_LOCALES.has(locale) ? 'rtl' : 'ltr';

  // Keep <html lang> + <html dir> aligned with the active locale so screen
  // readers, bidi browsers, and CSS logical-properties all behave correctly.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const html = document.documentElement;
    html.lang = locale;
    html.dir = dir;
  }, [locale, dir]);

  const t = useCallback(
    (key: MessageKey, vars?: Vars) => interpolate(resolveKey(locale, key), vars),
    [locale],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ locale, dir, setLocale, t }),
    [locale, dir, setLocale, t],
  );
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

// Stable fallback returned when useT is called outside an <I18nProvider>.
// Hoisted to module scope so the `t` reference is identical across renders;
// otherwise consumers that put `t` in a useEffect dep array would re-run on
// every render in test fixtures and cause RPC re-fetch loops.
const FALLBACK_T = (key: MessageKey, vars?: Vars) =>
  interpolate(resolveKey('fr', key), vars);
const FALLBACK_CTX: I18nContextValue = {
  locale: 'fr',
  dir: 'ltr',
  setLocale: () => {
    /* no-op outside provider */
  },
  t: FALLBACK_T,
};

/**
 * Returns the active i18n context. When called outside an `<I18nProvider>`
 * (e.g. unit tests rendering a page in isolation), we transparently fall
 * back to the default French catalogue so callers don't have to wrap every
 * test fixture. Production always sees the real provider via `main.tsx`.
 */
export function useT(): I18nContextValue {
  const ctx = useContext(I18nContext);
  return ctx ?? FALLBACK_CTX;
}

/** Test-only — used by Vitest to snapshot key resolution without React. */
export const __testing__ = { resolveKey, interpolate, pickInitialLocale };
